import React, { Component } from 'react';
// import { Link } from 'gatsby';
import parse from 'html-react-parser';
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  gql,
  NormalizedCacheObject,
  QueryOptions
} from '@apollo/client';
// import fetch from 'isomorphic-fetch';
import LoadingContainer from '@ecs/tng-common/components/loading-container/LoadingContainer';
import { ifMobile, scrollTo } from '@ecs/tng-common/scripts/helpers';
import ROUTES from '../helpers/routes';
import { truncateText } from '../helpers/functions';
import ByLine from './ByLine';
import Pagination from './Pagination';
import { ICategory, IPost } from '../helpers/interfaces';
// import { withApolloCustomize } from '../apollo/client';

export const latestPostsLimit = 12;

interface LatestPostsListProps {
  catSlug?: string | null;
  toHideCategory?: boolean;
  authId?: number | null;
  numberOfPosts: number;
  heading?: string;
  postsPerPage?: number;
  currentPath: string;
  initPosts: IPost[];
  className?: string;
  excludeIds?: number[];
}

interface LatestPostsListState {
  currentPage: number;
  // isClient: boolean;
  posts: IPost[];
  isLoading: boolean;
}

function getQueryParams() {
  if (typeof window === 'undefined') return 1;
  const urlParams = new URLSearchParams(window.location.search);
  const pageParam = urlParams.get('page');
  return pageParam ? parseInt(pageParam, 10) : 1;
}

function getValidPage(page: number, postsPerPage: number, numberOfPosts: number, limit: number) {
  const totalPages = Math.ceil(numberOfPosts / (postsPerPage || limit));
  return Math.max(1, Math.min(page, totalPages));
}

const GET_POSTS = gql`
  query GetPostsWithOffset(
    $limit: Int
    $skip: Int
    $order: String
    $categorySlug: String
    $authorId: Int
    $excludeIds: [Int]
  ) {
    postWithOffset(
      limit: $limit
      skip: $skip
      order: $order
      categorySlug: $categorySlug
      authorId: $authorId
      excludeIds: $excludeIds
    ) {
      databaseId
      title
      date
      slug
      uri
      excerpt
      author {
        node {
          firstName
          lastName
          uri
          slug
        }
      }
      categories {
        nodes {
          name
          uri
          databaseId
        }
      }
    }
  }
`;

class LatestPostsListComponent extends Component<LatestPostsListProps, LatestPostsListState> {
  private client: ApolloClient<NormalizedCacheObject>;

  constructor(props: LatestPostsListProps) {
    super(props);
    this.state = {
      currentPage: 1,
      // isClient: false,
      posts: this.props.initPosts,
      isLoading: false
    };

    // Create Apollo Client instance
    const httpLink = new HttpLink({
      uri: process.env.WPGRAPHQL_URL,
      fetch
    });

    this.client = new ApolloClient({
      link: ApolloLink.from([httpLink]),
      cache: new InMemoryCache()
    });
  }

  componentDidMount() {
    const initialPage = getValidPage(
      getQueryParams(),
      this.props.postsPerPage || latestPostsLimit,
      this.props.numberOfPosts,
      latestPostsLimit
    );
    if (this.state.currentPage !== initialPage) {
      this.setState({ isLoading: true });
      this.setState({ currentPage: initialPage }, () => {
        this.refetchPosts(this.state.currentPage);
      });
    }
  }

  /* componentDidUpdate(prevProps: LatestPostsListProps, prevState: LatestPostsListState) {
    if (prevState.currentPage !== this.state.currentPage) {
      this.refetchPosts(this.state.currentPage);
    }
  } */

  handlePageChange = (page: number) => {
    if (ifMobile()) {
      scrollTo('latest-posts', 0);
    }

    this.setState({ currentPage: page }, () => {
      // to update page param in the url
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        // Check if the 'page' parameter exists
        if (params.has('page')) {
          // Update the 'page' parameter
          params.set('page', page.toString());
          const newUrl = `${url.pathname}?${params.toString()}${url.hash}`;
          window.history.pushState({ path: newUrl }, '', newUrl);
        }
      }
      this.refetchPosts(this.state.currentPage);
    });
  };

  refetchPosts(page: number) {
    this.setState({ isLoading: true });
    const { postsPerPage, catSlug, authId, excludeIds } = this.props;
    const skip = (page - 1) * (postsPerPage || latestPostsLimit);
    const options: QueryOptions = {
      query: GET_POSTS,
      variables: {
        limit: postsPerPage || latestPostsLimit,
        skip,
        order: 'DESC',
        categorySlug: catSlug,
        authorId: authId,
        excludeIds: excludeIds
      }
    };
    const fetchPosts = (retryCount: number = 2) => {
      this.client
        .query(options)
        .then(({ data }) => {
          if (data && data.postWithOffset) {
            this.setState({ posts: data.postWithOffset, isLoading: false });
          } else if (retryCount > 0) {
            fetchPosts(retryCount - 1);
          } else {
            this.setState({ isLoading: false });
          }
        })
        .catch(() => {
          if (retryCount > 0) {
            fetchPosts(retryCount - 1);
          } else {
            this.setState({ isLoading: false });
          }
        });
    };
    fetchPosts(2); // max 2 attempts
  }

  render() {
    const {
      heading = 'The latest from our experts',
      numberOfPosts,
      postsPerPage = latestPostsLimit,
      currentPath,
      authId,
      toHideCategory
    } = this.props;
    const { posts, isLoading } = this.state;
    const getCategoryData = (post: IPost) => {
      let categoryData: ICategory | undefined;
      if (!toHideCategory) {
        const categoryNode = post?.categories?.nodes[0];
        if (categoryNode?.name && categoryNode?.uri) {
          categoryData = {
            name: categoryNode.name,
            uri: categoryNode.uri,
            databaseId: categoryNode?.databaseId
          };
        }
      }
      return categoryData;
    };
    return (
      <section
        className={`${this.props.className || ''} container container-py`}
        id="latest-posts">
        <div className="row gx-lg-6">
          <h2 className="heading-mb">{heading}</h2>
          {!isLoading && posts.length ? (
            posts.map((post: IPost, i: number) => {
              const excerpt = truncateText(post.excerpt ?? post.content ?? '', 90);
              // REPLACE WITH GATSBY LINK
              return (
                <div
                  className="col-12 col-md-6 col-lg-4 latest-posts-single"
                  key={post.databaseId}>
                  <div
                    className={`${i !== 0 ? 'border-top' : ''} pt-4 pb-3 h-100 d-flex flex-wrap`}>
                    <a
                      //  data-link-cs={`ae-mod-latest-c${post.databaseId}`}
                      href={ROUTES.BLOG.toUrl(post.uri ?? '')}
                      className="post-link-white w-100">
                      <h3 className="h5 text-dark">
                        <span className="post-link-title">{parse(post.title)}</span>
                      </h3>
                      <p className="text-gray mb-2 post-link-text">{parse(excerpt.trim())}</p>
                    </a>

                    <ByLine
                      className="align-self-end"
                      author={!authId ? post.author?.node : undefined}
                      date={post.date}
                      databaseId={post.databaseId}
                      category={getCategoryData(post)}
                      isAuthorLink={false}
                      authorImgLazyLoading
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="loading-wrapper position-relative">
              <LoadingContainer state />
            </div>
          )}
        </div>
        {numberOfPosts > postsPerPage && (
          <Pagination
            currentPage={this.state.currentPage}
            numberOfPosts={numberOfPosts}
            postsPerPage={postsPerPage}
            onPageChange={this.handlePageChange}
            className="mt-2"
            currentPath={currentPath}
          />
        )}
      </section>
    );
  }
}

export default LatestPostsListComponent;
